import { getPrimaryLabOptionColor } from "../../AppData/LabOptionColor";
import type { LabData } from "../../AppData/LabData";
import { getFabricBaseColor } from '../fabricColors';

/**
 * Product-specific 2D atlas hooks used by ModelViewerTextureUpdater.
 *
 * `pre` draws masks or base data before the Design Lab canvas; `post` adjusts
 * the completed atlas or optional FX canvas afterward. Most products need
 * neither hook and use these no-op defaults.
 */
export class ModelViewerTexture {
    pre = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string):void|Promise<void> => {}
    post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string):void|Promise<void> => {}
}

const load_image = (url: URL) => {
    const image = new Image()
    image.crossOrigin = "anonymous"
    image.src = url.href
    return image
};

const isImageLoaded = (image: HTMLImageElement|null|undefined): image is HTMLImageElement => {
    return !!image && image.complete && image.naturalWidth > 0 && image.naturalHeight > 0
}

const load_image_promise = (url: URL): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = "anonymous"
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error("Error loading image " + url.href))
        image.src = url.href
    })
}

export const Resources:Record<string,HTMLImageElement|null> = {
    'unisex-joggers-mask': null,
    'blanket_overlay_white': null,
    'blanket_overlay_black': null,
}

/**
 * Select the small amount of texture compositing that cannot be expressed by
 * the shared Lab canvas alone. The switch is keyed by stable product id for
 * the same reason as ModelViewerProduct's GLB registry.
 *
 * Loaded masks are retained in `Resources` for the page lifetime so repeated
 * edits and viewer mounts do not decode the same asset again.
 */
export default function useCustomProductTexture(product: number, labData: LabData): ModelViewerTexture {
    var modelViewerTexture = new ModelViewerTexture

    switch(product) {
        case 8: // Pillows: fill or mirror the non-designed half of the atlas.
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => { 
                if (productTextureCanvas == null) return
                let product_texture_context = productTextureCanvas.getContext("2d")
                if (product_texture_context === null) return
                product_texture_context.globalCompositeOperation = "source-over"
                const color = options['color'] || 'designed'
                switch(color) {
                    case "designed": // keep texture
                        break
                    case "printed": // redraw half offset
                        product_texture_context.drawImage(productTextureCanvas, 1024*resolution, 0);
                        break
                    default:
                        let colors = {
                            'white': '#ffffff',
                            'natural-white': '#ffffff',
                            'black': '#050505',
                            'red': '#952122',
                            'navy-blue': '#34375e',
                            'dark-brown': '#49321c',
                            'fuschia': '#a41759',
                            'bronze': '#421c08',
                            'dark-grey': '#5c7587',
                            'deep-purple': '#3f0c34',
                        } as any
                        product_texture_context.fillStyle = colors[color] || colors['white'];
						product_texture_context.fillRect(1024*resolution, 0, 1024*resolution, 1024*resolution);
                }
            }
            break
        case 13: // Scarves: build the fabric/thread FX atlas asynchronously.
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => {
                if (fxTextureCanvas == null) return
                let product_texture_context = fxTextureCanvas.getContext("2d")
                if (product_texture_context === null) return
                
                // Get Color
                const thread_colors = {
                    "white": "#ffffff",
                    "light-grey": "#a4a4a4",
                    "dark-grey": "#555555",
                    "black": "#2a2a2a",
                }
                const thread_color: keyof typeof thread_colors = options['thread-color'] ?? 'black'
                const fabric = (options as { fabric?: string }).fabric ?? 'cotton-silk'

                // Thread color mask - As usual gotta keep the full path in URL() uses.
                let thread_color_mask_slug = ["10x45", "16x72"].includes(variant) ? 'long-scarf-thread-color-mask' : 'square-scarf-thread-color-mask'
                let thread_color_mask_url = ["10x45", "16x72"].includes(variant) ? new URL('@resources/3d-models/textures/long-scarf-thread-color-mask.png', import.meta.url) : new URL('@resources/3d-models/textures/square-scarf-thread-color-mask.png', import.meta.url)

                const process_draw = async () => {
                    // Ensure images are available
                    Resources[thread_color_mask_slug] = isImageLoaded(Resources[thread_color_mask_slug]) ? Resources[thread_color_mask_slug] : await load_image_promise(thread_color_mask_url)

                    // Clear
                    product_texture_context.clearRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)

                    // Add fabric base color
                    product_texture_context.fillStyle = getFabricBaseColor(fabric)
                    product_texture_context.globalCompositeOperation = "source-over"
                    product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)

                    // Add texture
                    product_texture_context.globalCompositeOperation = "multiply"
                    product_texture_context.drawImage(productTextureCanvas, 0, 0)

                    // Mask out thread
                    product_texture_context.globalCompositeOperation = "destination-out"
                    product_texture_context.drawImage(Resources[thread_color_mask_slug] as CanvasImageSource, 0, 0, fxTextureCanvas.width, fxTextureCanvas.height)

                    // Add thread color
                    product_texture_context.fillStyle = thread_colors[thread_color]
                    product_texture_context.globalCompositeOperation = "destination-over"
                    product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
                }

                // The renderbot must not acknowledge the FX texture until its
                // asynchronously loaded thread mask has been composited.
                return process_draw()
            }
            break
        case 66: // Notebook: derive a monochrome FX canvas from the design.
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => { 
                if (fxTextureCanvas == null) return
                let product_texture_context = fxTextureCanvas.getContext("2d")
                if (product_texture_context === null) return
                
                product_texture_context.fillStyle = "#FFFFFF"
                product_texture_context.globalCompositeOperation = "source-over"
                product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
                product_texture_context.globalCompositeOperation = 'luminosity'
                product_texture_context.drawImage(productTextureCanvas, 0, 0)
            }
            break
        case 117: // Blanket: overlay thread-color-specific edge artwork.
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => { 
                if (productTextureCanvas == null) return
                let product_texture_context = productTextureCanvas.getContext("2d")
                if (product_texture_context === null) return

                const thread_color = options['thread-color'] ?? 'black'

                if (!Resources['blanket_overlay_white']) Resources['blanket_overlay_white'] = load_image(new URL('@resources/3d-models/textures/blanket_front_white.png', import.meta.url))
                if (!Resources['blanket_overlay_black']) Resources['blanket_overlay_black'] = load_image(new URL('@resources/3d-models/textures/blanket_front_black.png', import.meta.url))

                const blanket_overlay = Resources['blanket_overlay_'+thread_color]
                if (isImageLoaded(blanket_overlay)) {
                    product_texture_context.globalCompositeOperation = "source-over"
                    product_texture_context.drawImage(blanket_overlay, 0, 0, productTextureCanvas.width, productTextureCanvas.height)
                }
            }
            break
        case 122: //Permanent sticker
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => {
                if (fxTextureCanvas == null) return
                let product_texture_context = fxTextureCanvas.getContext("2d")
                if (product_texture_context === null) return

                product_texture_context.fillStyle = "#FFFFFF"
                product_texture_context.globalCompositeOperation = "source-over"
                product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
                product_texture_context.globalCompositeOperation = "destination-in"
                product_texture_context.drawImage(productTextureCanvas, 0, 0)
            }
            break
        case 126: //Unisex Polo shirt
        case 127: //Fitted Polo shirt
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => {
                if (fxTextureCanvas == null) return
                let product_texture_context = fxTextureCanvas.getContext("2d")
                if (product_texture_context === null) return

                if (!("color" in options)) return
                const color = getPrimaryLabOptionColor(
                    labData.options.color?.layout === 'color'
                        ? labData.options.color.colors[options.color]
                        : undefined
                ) ?? '#000000';

                product_texture_context.fillStyle = color
                product_texture_context.globalCompositeOperation = "source-over"
                product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
                product_texture_context.drawImage(productTextureCanvas, 0, 0)
                product_texture_context.fillRect(459*resolution, 675*resolution, 130*resolution, 176*resolution)
            }
            break
        case 128: //Unisex Joggers
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => {
                if (fxTextureCanvas == null) return
                let product_texture_context = fxTextureCanvas.getContext("2d")
                if (product_texture_context === null) return

                if (!("color" in options)) return
                const color = getPrimaryLabOptionColor(
                    labData.options.color?.layout === 'color'
                        ? labData.options.color.colors[options.color]
                        : undefined
                ) ?? '#000000';

                product_texture_context.clearRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)    
                product_texture_context.globalCompositeOperation = "source-over"
                product_texture_context.drawImage(productTextureCanvas, 0, 0)

                if(!Resources['unisex-joggers-mask']) Resources['unisex-joggers-mask'] = load_image(new URL('@resources/3d-models/textures/unisex-joggers-mask.png', import.meta.url))
                
                if (isImageLoaded(Resources['unisex-joggers-mask'])) {
                    product_texture_context.globalCompositeOperation = "destination-in"
                    product_texture_context.drawImage(Resources['unisex-joggers-mask'] as CanvasImageSource, 0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
                }

                product_texture_context.globalCompositeOperation = "destination-over"
                product_texture_context.fillStyle = color
                product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
            }
            break
        case 129: // Desk Mats
            modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => { 
                if (productTextureCanvas == null) return
                let product_texture_context = productTextureCanvas.getContext("2d")
                if (product_texture_context === null) return

                if (!Resources['desk_mat_9x8_overlay']) Resources['desk_mat_9x8_overlay'] = load_image(new URL('@resources/3d-models/textures/desk_mat_9_overlay.png', import.meta.url))
                if (!Resources['desk_mat_35x16_overlay']) Resources['desk_mat_35x16_overlay'] = load_image(new URL('@resources/3d-models/textures/desk_mat_35_overlay.png', import.meta.url))

                const desk_mat_overlay = Resources['desk_mat_'+variant+'_overlay']
                if (isImageLoaded(desk_mat_overlay)) {
                    product_texture_context.globalCompositeOperation = "source-over"
                    product_texture_context.drawImage(desk_mat_overlay, 0, 0, productTextureCanvas.width, productTextureCanvas.height)
                }
            }
            break
        case 131: // Coasters
        modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => { 
            if (productTextureCanvas == null) return
            let product_texture_context = productTextureCanvas.getContext("2d")
            if (product_texture_context === null) return

            if (!Resources['coaster-overlay']) Resources['coaster-overlay'] = load_image(new URL('@resources/3d-models/textures/coaster-overlay.png', import.meta.url))

            if (isImageLoaded(Resources['coaster-overlay'])) {
                product_texture_context.globalCompositeOperation = "multiply"
                product_texture_context.drawImage(Resources['coaster-overlay'] as CanvasImageSource, 0, 0, productTextureCanvas.width, productTextureCanvas.height)
            }
        }
        case 137: // Drawstring Bag
        modelViewerTexture.post = (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => { 
            if (fxTextureCanvas == null) return
            let product_texture_context = fxTextureCanvas.getContext("2d")
            if (product_texture_context === null) return
            
            product_texture_context.fillStyle = '#232323FF'
            product_texture_context.globalCompositeOperation = "source-over"
            product_texture_context.fillRect(0, 0, fxTextureCanvas.width, fxTextureCanvas.height)
            product_texture_context.drawImage(productTextureCanvas, 0, 0)
        }
        break
        case 138: // Printed Patch
        case 139: // Snapback with Printed Patch
        modelViewerTexture.post = async (productTextureCanvas: OffscreenCanvas, fxTextureCanvas: OffscreenCanvas|null, scene: Object, resolution: number, options: any, variant: string) => {
            if (productTextureCanvas == null) return
            const product_texture_context = productTextureCanvas.getContext("2d")
            if (product_texture_context === null) return

            const printed_patch_overlay_urls: Partial<Record<string, URL>> = {
                circle_2: new URL('@resources/3d-models/textures/printed-patch-overlay-circle_2.png', import.meta.url),
                circle_2_5: new URL('@resources/3d-models/textures/printed-patch-overlay-circle_2_5.png', import.meta.url),
                oval_2_75: new URL('@resources/3d-models/textures/printed-patch-overlay-oval_2_75.png', import.meta.url),
                oval_3_5: new URL('@resources/3d-models/textures/printed-patch-overlay-oval_3_5.png', import.meta.url),
                rectangle_2_75: new URL('@resources/3d-models/textures/printed-patch-overlay-rectangle_2_75.png', import.meta.url),
                rectangle_3_5: new URL('@resources/3d-models/textures/printed-patch-overlay-rectangle_3_5.png', import.meta.url),
                square_2: new URL('@resources/3d-models/textures/printed-patch-overlay-square_2.png', import.meta.url),
                square_2_5: new URL('@resources/3d-models/textures/printed-patch-overlay-square_2_5.png', import.meta.url),
            }
            const overlay_url = printed_patch_overlay_urls[variant]
            if (!overlay_url) return

            const overlay_key = 'printed-patch-overlay-' + variant
            const cached_overlay = Resources[overlay_key]
            const overlay = isImageLoaded(cached_overlay)
                ? cached_overlay
                : await load_image_promise(overlay_url)
            Resources[overlay_key] = overlay

            product_texture_context.globalCompositeOperation = "screen"
            product_texture_context.drawImage(overlay, 0, 0, productTextureCanvas.width, productTextureCanvas.height)
        }
        break
        case 144: // Ruffle Scrunchie: tint the cloth like the scarf FX atlas.
            modelViewerTexture.pre = (productTextureCanvas, ...args) => {
                const context = productTextureCanvas.getContext('2d')
                if(context === null) return
                const options = args[3] as { fabric?: string }

                context.globalCompositeOperation = 'source-over'
                context.fillStyle = getFabricBaseColor(options.fabric ?? 'matte-crepe')
                context.fillRect(0, 0, productTextureCanvas.width, productTextureCanvas.height)
                // The shared updater draws the artwork immediately after pre.
                context.globalCompositeOperation = 'multiply'
            }
            break
    }

    return modelViewerTexture
}
